import React, { useState, useCallback, useRef } from 'react';
import { Box, Grid, Typography, CircularProgress, Alert, FormControlLabel, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoIcon from '@mui/icons-material/Info';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission, useFileUpload } from '../../../hooks';
import {
  UploadContainer,
  UploadTitle,
  UploadButton,
  ChooseFileButton,
  MainContent
} from './ENO1TempUpload.styled';
import { ErrorTable } from '../../../common/components/ErrorTable';

// Define metadata interface
interface FileMetadata {
  fileId: string | null;
  fileName: string;
  operatorByFileUploader: string;
  accountingYear: number;
  accountingQuarter: number;
  fileVersion: number;
  creationDate: string;
  fileStatus: string;
  processDate: string | null;
  validStatus: string | null;
  pendingOn: string;
  loggedUserOperator: string;
  deletedAt: string | null;
  comment: string | null;
}

const ENO1TempUpload: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [fileUploaded, setFileUploaded] = useState<boolean>(false);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [extrapolateChecked, setExtrapolateChecked] = useState<boolean>(true); // Default to true
  const [showExtrapolateCheckbox, setShowExtrapolateCheckbox] = useState<boolean>(false); // Control checkbox visibility
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Add snackbar state for notifications (same pattern as Homepage)
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { hasPermission, isLoading } = usePermission('/eno1-temp-upload', '/upload');
  
  // Use the file upload hook
  const { 
    selectedFile,
    isUploading,
    uploadError,
    validationErrors,
    handleFileChange,
    handleUpload,
    uploadToEndpoint,
    resetUpload
  } = useFileUpload({
    onUploadSuccess: (response: any) => {
      console.log('Full upload success response:', JSON.stringify(response, null, 2));
      
      // Store metadata from response
      if (response && response.metadata) {
        setFileMetadata(response.metadata);
      }
      
      // Handle different response formats
      let extrapolateValue = null;
      
      if (typeof response === 'boolean') {
        // Response is a direct boolean value (true/false)
        extrapolateValue = response;
        console.log('Response is direct boolean:', extrapolateValue);
      } else if (response && response.hasOwnProperty('extrapolate')) {
        // Response is an object with extrapolate field
        extrapolateValue = response.extrapolate;
        console.log('Found extrapolate in response root:', extrapolateValue);
      } else if (response && response.data && response.data.hasOwnProperty('extrapolate')) {
        // Response is nested in data object
        extrapolateValue = response.data.extrapolate;
        console.log('Found extrapolate in response.data:', extrapolateValue);
      } else {
        console.log('No extrapolate field found in response');
      }
      
      if (extrapolateValue !== null) {
        const boolValue = Boolean(extrapolateValue);
        setExtrapolateChecked(boolValue);
        setShowExtrapolateCheckbox(boolValue);
        console.log('Setting extrapolate to:', boolValue, 'Show checkbox:', boolValue);
      } else {
        // No extrapolate field in response - default to showing checkbox
        setExtrapolateChecked(true);
        setShowExtrapolateCheckbox(true);
        console.log('No extrapolate field, defaulting to show checkbox: true');
      }
      
      // Mark that initial file upload was successful
      setFileUploaded(true);
    },
    onUploadError: (error: any) => {
      console.error('Upload error:', error);
      
      // Show error notification
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to upload file. Please try again.',
        severity: 'error',
      });
    },
    allowedFileTypes: ['.xlsx', '.xls', '.csv'], // Allow Excel and CSV files
    maxFileSizeMB: 5, // 5MB max file size
    uploadEndpoint: '/sampling-planning/extrapolate' // Using extrapolate endpoint
  });
  
  // Handle temporary record upload
  const handleTemporaryRecord = useCallback(async () => {
    try {
      const extrapolateValue = extrapolateChecked ? 'true' : 'false';
      const result = await uploadToEndpoint(`/sampling-planning/upload/temp?extrapolate=${extrapolateValue}`);
      if (result) {
        setSnackbar({
          open: true,
          message: 'File has been successfully recorded as temporary!',
          severity: 'success',
        });
        navigate(`/eno1-temp-overview`);
        setFileUploaded(false);
        setFileMetadata(null);
        setExtrapolateChecked(true); // Reset to default
        setShowExtrapolateCheckbox(false); // Hide checkbox
        // Completely reset the file selection
        resetUpload();
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Error recording as temporary:', error);
      setSnackbar({
        open: true,
        message: 'Failed to record file as temporary. Please try again.',
        severity: 'error',
      });
    }
  }, [uploadToEndpoint, resetUpload, navigate, extrapolateChecked]);
  
  // Handle Cancel action - just clear everything, no API request
  const handleCancelUpload = useCallback(() => {
    // Reset all states to default
    resetUpload();
    setFileUploaded(false);
    setFileMetadata(null);
    setExtrapolateChecked(true); // Reset to default
    setShowExtrapolateCheckbox(false); // Hide checkbox
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    setSnackbar({
      open: true,
      message: 'Upload has been cancelled.',
      severity: 'info',
    });
  }, [resetUpload]);
  
  // Get all errors to display in the table
  const allErrors = React.useMemo(() => {
    const errors = [...validationErrors];
    
    // Add uploadError to the errors list if it exists
    if (uploadError) {
      // Convert generic error to ValidationError format
      if (!validationErrors.length) {
        // Only add general error if there are no specific validation errors
        errors.push({
          sampleId: null,
          field: 'General',
          errorCode: uploadError.code || 'ERROR',
          message: uploadError.message,
          version: null
        });
        
        // Add any details as separate error entries
        if (uploadError.details && uploadError.details.length > 0) {
          uploadError.details.forEach(detail => {
            errors.push({
              sampleId: null,
              field: 'Detail',
              errorCode: 'INFO',
              message: detail,
              version: null
            });
          });
        }
        
        // Add validationErrors from uploadError if they exist
        if (uploadError.validationErrors && uploadError.validationErrors.length > 0) {
          errors.push(...uploadError.validationErrors);
        }
      }
    }
    
    return errors;
  }, [uploadError, validationErrors]);
  
  // Handle snackbar close (same pattern as Homepage)
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleChooseFileClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleInitialUpload = async () => {
    try {
      await handleUpload();
    } catch (error) {
      console.error('Error in initial upload:', error);
      setSnackbar({
        open: true,
        message: 'Failed to upload file. Please try again.',
        severity: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasPermission) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="warning">You don't have permission to access this page</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="eno1-temp-upload-container">
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='ENO1 Temp Upload'
            subtitle='Sampling Planning'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            data-testid="eno1-temp-upload-section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="eno1-temp-upload-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent data-testid="eno1-temp-upload-main-content">
            <UploadContainer data-testid="eno1-temp-upload-content">
              <UploadTitle variant="h4" data-testid="eno1-temp-upload-title">Upload Temp Overview File</UploadTitle>
              
              {/* File Upload Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }} data-testid="eno1-temp-upload-form">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept=".xlsx,.xls,.csv"
                  data-testid="eno1-temp-upload-file-input"
                />
                
                <ChooseFileButton
                  variant="outlined"
                  onClick={handleChooseFileClick}
                  startIcon={<CloudUploadIcon />}
                  disabled={isUploading || fileUploaded}
                  data-testid="eno1-temp-upload-choose-file-btn"
                >
                  Choose File
                </ChooseFileButton>

                <UploadButton
                  variant="contained"
                  onClick={handleInitialUpload}
                  disabled={!selectedFile || isUploading || fileUploaded}
                  data-testid="eno1-temp-upload-upload-btn"
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </UploadButton>

                {selectedFile && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2 }}>
                    {selectedFile.name}
                  </Typography>
                )}

                {isUploading && (
                  <CircularProgress size={20} sx={{ ml: 2 }} />
                )}
              </Box>


              {/* Success Message - Show when no errors found after upload */}
              {fileUploaded && allErrors.length === 0 && (
                <Box sx={{ mt: 3 }} data-testid="eno1-temp-upload-success-message">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      No errors have been found in the file {selectedFile?.name || 'Unknown File'}.
                    </Typography>
                  </Box>
                  
                  {/* Extrapolate Checkbox - Only show if response allows it */}
                  {showExtrapolateCheckbox && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={extrapolateChecked}
                          onChange={(e) => setExtrapolateChecked(e.target.checked)}
                          color="primary"
                          data-testid="extrapolate-checkbox"
                        />
                      }
                      label="Extrapolate to the full quarter"
                      sx={{ 
                        mt: 1,
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                          color: 'text.secondary'
                        }
                      }}
                    />
                  )}
                </Box>
              )}

              {/* Action Buttons - Show when file is uploaded and no errors */}
              {fileUploaded && allErrors.length === 0 && (
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }} data-testid="eno1-temp-upload-action-buttons">
                  <UploadButton
                    variant="contained"
                    onClick={handleTemporaryRecord}
                    disabled={isUploading}
                    data-testid="eno1-temp-upload-record-temporary-btn"
                  >
                    Record in the system as temporary
                  </UploadButton>
                  
                  <ChooseFileButton
                    variant="outlined"
                    onClick={handleCancelUpload}
                    disabled={isUploading}
                    data-testid="eno1-temp-upload-cancel-btn"
                  >
                    Cancel
                  </ChooseFileButton>
                </Box>
              )}

              {/* Error Table - Show validation errors if any exist */}
              {allErrors.length > 0 && (
                <Box sx={{ mt: 3 }} data-testid="eno1-temp-upload-errors">
                  <ErrorTable 
                    errors={allErrors}
                    fileName={selectedFile?.name || 'Unknown File'}
                    maxWidth="100%"
                  />
                </Box>
              )}

            </UploadContainer>

            {/* Notification Snackbar - Same pattern as Homepage */}
            <NotificationSnackbar
              open={snackbar.open}
              message={snackbar.message}
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              data-testid="eno1-temp-upload-notification"
            />
          </MainContent>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ENO1TempUpload; 